import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPackage, FiChevronRight, FiFilter } from 'react-icons/fi';
import { useLanguage } from '../../context/LanguageContext';
import orderService from '../../services/order.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const statusColors = {
  pending: 'warning',
  confirmed: 'info',
  processing: 'info',
  shipped: 'primary',
  delivered: 'success',
  cancelled: 'danger',
  refunded: 'secondary',
};

const paymentStatusColors = {
  pending: 'warning',
  paid: 'success',
  failed: 'danger',
  refunded: 'secondary',
};

const OrderHistoryPage = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 10 };
        if (statusFilter) params.status = statusFilter;
        const { data } = await orderService.getMyOrders(params);
        setOrders(data.data);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [page, statusFilter]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  return (
    <div className="section-padding">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>{t("myOrders")}</h2>
          <div className="d-flex align-items-center gap-2">
            <FiFilter className="text-muted" />
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">{t("allOrders")}</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : orders.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {orders.map((order) => {
              const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
              return (
                <Link
                  key={order.id}
                  to={`/account/orders/${order.id}`}
                  className="dashboard-card text-decoration-none text-dark"
                  style={{ transition: 'box-shadow 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = ''}
                >
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <FiPackage className="text-primary" />
                        <strong>{order.orderNumber}</strong>
                      </div>
                      <small className="text-muted">
                        Placed on {new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric',
                        })}
                      </small>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                      <div className="text-end">
                        <span className={`badge bg-${statusColors[order.status] || 'secondary'} mb-1`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                        <br />
                        <span className={`badge bg-${paymentStatusColors[order.paymentStatus] || 'secondary'} bg-opacity-75`}>
                          Payment: {order.paymentStatus}
                        </span>
                      </div>
                      <FiChevronRight className="text-muted" />
                    </div>
                  </div>

                  {/* Items preview */}
                  <div className="d-flex align-items-center gap-2 mt-3 pt-3 border-top">
                    <div className="d-flex gap-1">
                      {items?.slice(0, 3).map((item, i) => (
                        <img
                          key={i}
                          src={item.image || '/placeholder.png'}
                          alt={item.name}
                          className="rounded border"
                          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                        />
                      ))}
                      {items?.length > 3 && (
                        <div className="rounded border d-flex align-items-center justify-content-center bg-light"
                          style={{ width: '40px', height: '40px' }}>
                          <small className="text-muted">+{items.length - 3}</small>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1 text-muted small">
                      {items?.length} item{items?.length !== 1 ? 's' : ''}
                    </div>
                    <strong className="text-primary fs-5">${parseFloat(order.totalAmount).toFixed(2)}</strong>
                  </div>
                </Link>
              );
            })}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <nav className="d-flex justify-content-center mt-3">
                <ul className="pagination">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(page - 1)}>Previous</button>
                  </li>
                  {Array.from({ length: pagination.pages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= pagination.pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(page + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        ) : (
          <div className="text-center py-5">
            <FiPackage size={64} className="text-muted mb-3" />
            <h4>{t("noOrdersYet")}</h4>
            <p className="text-muted mb-4">
              {statusFilter ? 'No orders match this filter.' : "You haven't placed any orders yet."}
            </p>
            <Link to="/products" className="btn btn-fd-primary">Start Shopping</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryPage;
